import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Modal,
  Paper,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  TextField,
  Autocomplete,
  Box,
  InputAdornment, Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { IoCheckmarkCircle, IoGitMerge, IoCreateOutline, IoBan, IoTrash } from "react-icons/io5";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiButton from "components/ui/VuiButton";
import VuiBadge from "components/ui/VuiBadge";
import VuiPaginationControls from "components/ui/VuiPagination/custom/VuiCustomPagination";
import PorterTable from "components/PorterTable";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import { adminService } from "services/adminService";
import useDebounce from "../../../../hooks/useDebounce";
import { CircularProgress } from "@mui/material";
import { useSnackbar } from 'notistack';
import Grid from "@mui/material/Grid";
import CompanySelector from "../../../../components/EntitySelector/CompanySelector";
import { ACCENT_COLORS } from "utils/designTokens";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  maxWidth: "900px",
  height: "80vh",
  bgColor: "rgba(15, 22, 56, 0.95)",
  border: `2px solid ${ACCENT_COLORS.highlight}`,
  borderRadius: "12px",
  boxShadow: 24,
  p: 3,
  display: "flex",
  flexDirection: "column",
};

function ProductReview() {
  const [productsPage, setProductsPage] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for company selection
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);

  // State for the modals
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("details");

  // State for merging
  const [mergeTarget, setMergeTarget] = useState(null);
  const [allVerifiedProducts, setAllVerifiedProducts] = useState([]);

  // Pagination and Search State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [isEditingName, setIsEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar(); // For notifications

  // Track the last used search term
  const searchTermRef = useRef("");


  useEffect(() => {
    const fetchAllCompanies = async () => {
      setCompanyLoading(true);
      try {
        const response = await adminService.getAllCompanies();
        setAllCompanies(response.data || []);
        // Select the first company by default
        if (response.data && response.data.length > 0) {
          setSelectedCompanyId(response.data[0].id);
        }
      } catch (error) {
        console.error("Failed to fetch companies:", error);
      } finally {
        setCompanyLoading(false);
      }
    };

    fetchAllCompanies();
  }, []);

  const fetchProducts = useCallback(async (page, size, search) => {
    if (!selectedCompanyId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await adminService.getAllProducts(selectedCompanyId, page, size, search);
      setProductsPage(response.data || { content: [], totalPages: 0, totalElements: 0 });
    } catch (err) {
      setError("Failed to fetch products.");
      setProductsPage({ content: [], totalPages: 0, totalElements: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    // If the search term changes -> fetch page 0
    const pageToFetch = debouncedSearchTerm !== searchTermRef.current ? 0 : currentPage;

    fetchProducts(pageToFetch, pageSize, debouncedSearchTerm);

    // After the effect runs, update a ref with the current search term
    // This allows to compare the *next* debouncedSearchTerm with the one that
    // triggered the *last* fetch. A ref is used because updating it does not trigger a re-render
    searchTermRef.current = debouncedSearchTerm;

  }, [debouncedSearchTerm, currentPage, pageSize, fetchProducts, selectedCompanyId]);


  // Handlers for API actions
  const handleToggleStatus = async (product) => {
    try {
      if (product.status === "VERIFIED") {
        await adminService.disapproveProduct(product.id);
      } else {
        await adminService.approveProduct(product.id);
      }
      // Refresh the table data
      fetchProducts(currentPage, pageSize, debouncedSearchTerm);
    } catch (err) {
      console.error("Failed to update product status:", err);
    }
  };

  const handleOpenMergeModal = async (product) => {
    setSelectedProduct({ product });
    setModalMode("merge");
    setIsModalOpen(true);
    try {
      // Fetch ALL verified products for selection (large page size number)
      const response = await adminService.getAllProducts(selectedCompanyId, 0, 1000);
      setAllVerifiedProducts(response.data.content
        .filter(p => p.status === "VERIFIED" && p.id !== product.id));
    } catch (err) {
      console.error("Could not fetch products for merge target selection");
    }
  };

  const handleMerge = async () => {
    if (!selectedProduct || !mergeTarget) return;
    try {
      await adminService.mergeProduct(selectedProduct.product.id, mergeTarget.id);
      fetchProducts(currentPage, pageSize, debouncedSearchTerm);
      handleCloseModal();
    } catch (err) {
      console.error("Failed to merge product:", err);
    }
  };

  const handleUpdateDisplayName = async () => {
    if (!selectedProduct || !newDisplayName || newDisplayName === selectedProduct.product.displayName) {
      setIsEditingName(false);
      return;
    }
    setDetailLoading(true);
    try {
      await adminService.updateProductDetails(selectedProduct.product.id, newDisplayName);
      enqueueSnackbar("Product name updated successfully!", { variant: 'success' });
      handleCloseModal();
      fetchProducts(currentPage, pageSize, debouncedSearchTerm);
    } catch (err) {
      enqueueSnackbar("Failed to update product name.", { variant: 'error' });
    } finally {
      setDetailLoading(false);
      setIsEditingName(false);
    }
  };

  const handleRemoveAlias = async (aliasToRemove) => {
    setDetailLoading(true);
    try {
      await adminService.removeAlias(selectedProduct.product.id, aliasToRemove);
      // Refresh the modal with updated data
      const response = await adminService.getProductReviewDetails(selectedProduct.product.id);
      setSelectedProduct(response.data);
      enqueueSnackbar(`Alias '${aliasToRemove}' removed.`, { variant: 'info' });
    } catch (err) {
      enqueueSnackbar("Failed to remove alias.", { variant: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteAction = async (product) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${product.displayName}"? This may affect existing post analyses.`
    );
    if (confirmed) {
      try {
        await adminService.deleteProduct(product.id);
        enqueueSnackbar("Product deleted.", { variant: 'success' });
        fetchProducts(currentPage, pageSize, debouncedSearchTerm);
      } catch (err) {
        console.error("Delete failed:", err); // Added for debugging
        const errorMsg = err.response?.data?.message || "Failed to delete product.";
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    }
  };

  const handleOpenDetailsModal = async (product) => {
    try {
      const response = await adminService.getProductReviewDetails(product.id);
      setSelectedProduct(response.data);
      setModalMode("details");
      setIsModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch product details:", err);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setMergeTarget(null);
  };

  const handleSplitAlias = async (sourceProductId, alias) => {
    try {
      await adminService.splitAlias(sourceProductId, alias);
      // Refresh details in modal
      handleOpenDetailsModal({ id: sourceProductId });
      // Refresh main product list
      fetchProducts(currentPage, pageSize, debouncedSearchTerm);
    } catch (err) {
      console.error("Failed to split alias:", err);
    }
  };


  const columns = [
    { key: "product", name: "Product", align: "left" },
    { key: "status", name: "Status", align: "center" },
    { key: "aliases", name: "Aliases", align: "left" },
    { key: "actions", name: "Actions", align: "center" },
  ];

  const rows = productsPage.content.map((product) => ({
    product: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {product.displayName}
      </VuiTypography>
    ),
    status: (
      <VuiBadge
        variant="standard"
        badgeContent={product.status === "VERIFIED" ? "Verified" : "Needs Review"}
        color={product.status === "VERIFIED" ? "success" : "warning"}
        size="xs"
        container
      />
    ),
    aliases: (
      <VuiBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: "550px" }}>
        {product.aliases?.map((alias) => (
          <Chip
            key={alias}
            label={alias}
            size="small"
            sx={{
              backgroundColor: (theme) => theme.palette.grey[700],
              color: (theme) => theme.palette.white.main,
              border: (theme) => `1px solid ${theme.palette.grey[600]}`,
              fontSize: "0.75rem",
              height: "22px",
            }}
          />
        ))}
      </VuiBox>
    ),
    actions: (
      <VuiBox display="flex" justifyContent="center" gap={1}>
        <Tooltip title={product.status === "VERIFIED" ? "Mark as Needs Review" : "Approve"}>
          <IconButton onClick={() => handleToggleStatus(product)} size="small">
            {product.status === "VERIFIED" ? <IoBan color="orange" /> : <IoCheckmarkCircle color="green" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Merge into another product">
          <IconButton onClick={() => handleOpenMergeModal(product)} size="small">
            <IoGitMerge color={ACCENT_COLORS.highlight} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit Details / Split">
          <IconButton onClick={() => handleOpenDetailsModal(product)} size="small">
            <IoCreateOutline color="yellow" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Product">
          <IconButton onClick={() => handleDeleteAction(product)} size="small">
            <IoTrash color="red" />
          </IconButton>
        </Tooltip>
      </VuiBox>
    ),
  }));

  return (
    <React.Fragment>
      <Card sx={{ position: 'relative'}}>
        <VuiBox p={2} display="flex" justifyContent="space-between" alignItems="center">
          <VuiTypography variant="h6" color="white" fontWeight="medium">
            Product Curation
          </VuiTypography>
          <VuiBox display="flex" alignItems="center" gap={3}>
            <CompanySelector
              allCompanies={allCompanies}
              selectedCompanyId={selectedCompanyId}
              onCompanyChange={setSelectedCompanyId}
              loading={companyLoading}
              disabled={loading}
            />
            <TextField
              placeholder="Search by name..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </VuiBox>
        </VuiBox>
        {loading && !productsPage.content.length ? (
          <StandardLoadingIndicator />
        ) : error ? (
          <StandardMessageDisplay type="error" />
        ) : productsPage.content.length === 0 ? (
          <StandardMessageDisplay type="noData" message="No products found." />
        ) : (
          <>
            <PorterTable columns={columns} rows={rows} maxHeight="800px" />
            <VuiPaginationControls
              currentPage={currentPage}
              totalPages={productsPage.totalPages}
              totalElements={productsPage.totalElements}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              isLoading={loading}
            />
          </>
        )}
      </Card>
      {/* Modal implementation */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Paper sx={modalStyle}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiTypography variant="h6" sx={{mb : 1}}>
              {modalMode === "details" ? `Reviewing: ${selectedProduct?.product.displayName}` : `Merge: ${selectedProduct?.product.displayName}`}
            </VuiTypography>
            <IconButton onClick={handleCloseModal} size="medium"><CloseIcon sx={{ color: "#ed7070" }} /></IconButton>
          </VuiBox>
          <VuiBox sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {modalMode === "details" && selectedProduct && (
              <Box>
                {/* Product Name Editing */}
                <VuiBox display="flex" alignItems="center" mb={2}>
                  {isEditingName ? (
                    <>
                      <TextField
                        fullWidth
                        size="medium"
                        defaultValue={selectedProduct.product.displayName}
                        onChange={(e) => setNewDisplayName(e.target.value)}
                        variant="outlined"
                        sx={{
                          "& .MuiInputBase-root": {
                            border: "1px solid #56577A",
                            borderRadius: "8px",
                            "& fieldset": {
                              border: "none",
                            },
                          },
                        }}
                      />
                      <VuiButton onClick={handleUpdateDisplayName} color="success" size="small" sx={{ ml: 1 }}>Save</VuiButton>
                      <VuiButton onClick={() => setIsEditingName(false)} variant="outlined" color="error" size="small" sx={{ ml: 1 }}>Cancel</VuiButton>
                    </>
                  ) : (
                    <>
                      <VuiTypography variant="h5">{selectedProduct.product.displayName}</VuiTypography>
                      <Tooltip title="Edit name">
                        <IconButton
                          onClick={() => {
                            setIsEditingName(true);
                            setNewDisplayName(selectedProduct.product.displayName);
                          }}
                          size="small"
                          sx={{
                            ml: 1,
                            color: "text.secondary",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.1)",
                              color: "primary.main",
                            },
                          }}
                        >
                          <IoCreateOutline />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </VuiBox>

                {/* Alias and Mention Lists */}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <VuiTypography variant="button" color="text" fontWeight="bold" textTransform="uppercase">
                      Sanitized Aliases ({selectedProduct.product.aliases.length})
                    </VuiTypography>
                    <Paper sx={{ maxHeight: 300, overflow: 'auto', background: 'rgba(255,255,255,0.05)', p: 1, mt: 1 }}>
                      <List>
                        {selectedProduct.product.aliases.map(alias => (
                          <ListItem key={alias} sx={{ p: 0, mb: 0.5 }}>
                            <Chip
                              label={alias}
                              size="medium"
                              onDelete={() => handleRemoveAlias(alias)}
                              sx={{ mt: 1 }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <VuiTypography variant="button" color="text" fontWeight="bold" textTransform="uppercase">
                      Original Mentions ({selectedProduct.originalMentions.length})
                    </VuiTypography>
                    <Paper sx={{ maxHeight: 300, overflow: 'auto', background: 'rgba(255,255,255,0.05)', p: 1, mt: 1 }}>
                      <List>
                        {selectedProduct.originalMentions.map(mention => (
                          <ListItem key={mention} secondaryAction={
                            <VuiButton
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleSplitAlias(selectedProduct.product.id, mention)}>
                              Split
                            </VuiButton>
                          }>
                            <ListItemText primary={mention} sx={{ mt: 1.8 }} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
                {detailLoading && <CircularProgress size={24} sx={{ position: 'absolute', top: 20, right: 60 }} />}
              </Box>
            )}
            {modalMode === "merge" && (
              <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <VuiTypography variant="body2" mb={6}>
                  Select a verified product to merge into. This product's aliases will be added to the target, and this
                  product will be deleted.
                </VuiTypography>
                <Autocomplete
                  openOnFocus
                  options={allVerifiedProducts}
                  getOptionLabel={(option) => option.displayName || ""}
                  onChange={(event, newValue) => setMergeTarget(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  loading={loading}
                  popupIcon={
                    <ExpandMoreIcon sx={{ fontSize: 32, color: "primary.main" }} />
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Target Product"
                      variant="outlined"
                      sx={{
                        p: 2,
                        "& .MuiInputLabel-root": { fontWeight: 410, fontSize: 18 },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  )}
                  sx={{
                    p: 0.5,
                    "& .MuiAutocomplete-popupIndicator": {
                      transform: "none !important",
                    },
                  }}
                />
                <VuiBox mt="auto" pt={3} display="flex" justifyContent="flex-end" gap={1}>
                  <VuiButton onClick={handleCloseModal} variant="outlined" color="error">Cancel</VuiButton>
                  <VuiButton onClick={handleMerge} color="info" disabled={!mergeTarget}>Merge Product</VuiButton>
                </VuiBox>
              </Box>
            )}
          </VuiBox>
        </Paper>
      </Modal>
    </React.Fragment>
  );
}

export default ProductReview;